import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from 'react';
import './SlideSplit.css';

export interface SlideSplitProps {
  /** 大图的宽度 (px)，默认 300 */
  w?: number;
  /** 大图的高度 (px)，默认 150 */
  h?: number;
  /** 小图拼图块基础尺寸 (px)，默认 50 */
  l?: number;
  /** 小图拼图块圆弧半径 (px)，默认 0 */
  r?: number;
  /** 滑块提示文字 */
  sliderText?: string;
  /** 大图 URL */
  bigImg?: string;
  /** 小图 URL */
  smImg?: string;
  /** 拖动判定误差范围 */
  accuracy?: number;
  /** 滑动结果判定回调 */
  onSliderJudge?: (left: number) => void;
}

export interface SlideSplitRef {
  /** 重置滑块状态及画布 */
  reset: () => void;
}

const SlideSplit = forwardRef<SlideSplitRef, SlideSplitProps>((props, ref) => {
  const {
    w = 300,
    h = 150,
    l = 50,
    r = 0,
    sliderText = '拖动滑块向右>>',
    bigImg,
    smImg,
    onSliderJudge,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const blockRef = useRef<HTMLCanvasElement | null>(null);

  const sw = l + r * 2;
  const blockY = (h - sw) / 2;

  const [sliderLeft, setSliderLeft] = useState<number>(0);
  const [currentText, setCurrentText] = useState<string>(sliderText);
  const [resultMask, setResultMask] = useState<{ class: string; height: number }>({
    class: '',
    height: 0,
  });

  const isSliding = useRef<boolean>(false);
  const originRef = useRef<{ originX: number; originY: number }>({
    originX: 0,
    originY: 0,
  });

  // 更新文本初始值
  useEffect(() => {
    setCurrentText(sliderText);
  }, [sliderText]);

  // 加载与绘制图片
  useEffect(() => {
    const canvas = canvasRef.current;
    const block = blockRef.current;
    if (!canvas || !block) return;

    const canvasCtx = canvas.getContext('2d');
    const blockCtx = block.getContext('2d');

    if (block) {
      block.style.top = `${blockY}px`;
    }

    if (bigImg) {
      const img = document.createElement('img');
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        if (canvasCtx) {
          canvasCtx.clearRect(0, 0, w, h);
          canvasCtx.drawImage(img, 0, 0, w, h);
        }
      };
      img.src = bigImg;
    }

    if (smImg) {
      const img = document.createElement('img');
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        if (blockCtx) {
          blockCtx.clearRect(0, 0, sw, sw);
          blockCtx.drawImage(img, 0, 0, sw, sw);
        }
      };
      img.src = smImg;
    }
  }, [bigImg, smImg, w, h, sw, blockY]);

  // 快捷获取 Touch/Mouse 坐标点
  const getEventPoint = (e: MouseEvent | TouchEvent) => {
    if ('touches' in e && e.touches.length > 0) {
      return e.touches[0];
    }
    if ('changedTouches' in e && e.changedTouches.length > 0) {
      return e.changedTouches[0];
    }
    if ('pageX' in e) {
      return e;
    }
    return null;
  };

  const handleSliderDown = (e: React.MouseEvent | React.TouchEvent) => {
    const point = getEventPoint(e.nativeEvent);
    if (!point) return;

    isSliding.current = true;
    setCurrentText('');
    originRef.current = {
      originX: point.pageX,
      originY: point.pageY,
    };
  };

  useEffect(() => {
    const handleSliderMove = (e: MouseEvent | TouchEvent) => {
      if (!isSliding.current) return;
      const point = getEventPoint(e);
      if (!point) return;

      if (e.cancelable) {
        e.preventDefault();
      }

      let moveX = point.pageX - originRef.current.originX;
      const sliderWidth = 44;
      const maxMoveX = w - sliderWidth;

      if (moveX < 0) moveX = 0;
      if (moveX > maxMoveX) moveX = maxMoveX;

      setSliderLeft(moveX);

      const blockMaxMove = w - sw;
      const ratio = maxMoveX > 0 ? blockMaxMove / maxMoveX : 0;
      const blockLeft = moveX * ratio;

      if (blockRef.current) {
        blockRef.current.style.left = `${blockLeft}px`;
      }
    };

    const handleSliderUp = () => {
      if (!isSliding.current) return;
      isSliding.current = false;
      setResultMask((prev) => ({ ...prev, height: h / 2 }));

      if (blockRef.current) {
        const left = parseInt(blockRef.current.style.left, 10) || 0;
        onSliderJudge?.(left);
      }
    };

    document.addEventListener('mousemove', handleSliderMove);
    document.addEventListener('mouseup', handleSliderUp);
    document.addEventListener('touchmove', handleSliderMove, { passive: false });
    document.addEventListener('touchend', handleSliderUp);
    document.addEventListener('touchcancel', handleSliderUp);

    return () => {
      document.removeEventListener('mousemove', handleSliderMove);
      document.removeEventListener('mouseup', handleSliderUp);
      document.removeEventListener('touchmove', handleSliderMove);
      document.removeEventListener('touchend', handleSliderUp);
      document.removeEventListener('touchcancel', handleSliderUp);
    };
  }, [w, sw, h, onSliderJudge]);

  useImperativeHandle(ref, () => ({
    reset() {
      const canvas = canvasRef.current;
      const block = blockRef.current;
      if (canvas) {
        const canvasCtx = canvas.getContext('2d');
        canvasCtx?.clearRect(0, 0, w, h);
      }
      if (block) {
        const blockCtx = block.getContext('2d');
        blockCtx?.clearRect(0, 0, sw, sw);
        block.style.left = '0px';
      }
      setCurrentText(sliderText);
      setResultMask({ class: '', height: 0 });
      setSliderLeft(0);
    },
  }));

  return (
    <div className="slider-container" style={{ width: `${w}px` }}>
      <div className="slider-canvas" style={{ width: `${w}px`, height: `${h}px` }}>
        {/* 大图 */}
        <canvas width={w} height={h} ref={canvasRef} />
        {/* 小图 */}
        <canvas className="slider-block" width={sw} height={sw} ref={blockRef} />
        {/* 结果遮罩层 */}
        <div
          className={`result-mask ${resultMask.class}`}
          style={{ height: `${resultMask.height}px` }}
        />
      </div>
      {/* 拖动的滑块内容 */}
      <div className="slider-square">
        <div className="box">
          <button
            type="button"
            className="slider-square-icon"
            style={{ left: `${sliderLeft}px` }}
            onTouchStart={handleSliderDown}
            onMouseDown={handleSliderDown}
          >
            <svg className="w-5 h-5 text-[#1a1a24]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </button>
          <span>{currentText}</span>
        </div>
      </div>
    </div>
  );
});

SlideSplit.displayName = 'SlideSplit';

export default SlideSplit;
